'use client'

import React, {useState, useEffect} from 'react'
import classes from './BlogTOC.module.css'
import Image from 'next/image'
import minusImg from 'v1/assets/icons/footer/minus-icon.svg'
import plusImg from 'v1/assets/icons/footer/plus-icon.svg'

interface TOCEntry {
  id: string
  title: string
  level: number
  position: number
  children: TOCEntry[]
}

interface FlatTOCEntry {
  id: string
  title: string
  level: number
  position: number
}

interface BlogTOCProps {
  content: string
  title?: string
}

const BlogTOC: React.FC<BlogTOCProps> = ({content, title = 'IN THIS ARTICLE:'}) => {
  const [tocEntries, setTocEntries] = useState<TOCEntry[]>([])
  const [flatEntries, setFlatEntries] = useState<FlatTOCEntry[]>([])
  const [activeSection, setActiveSection] = useState<string | null>('introduction')
  const [isOpen, setIsOpen] = useState(true)

  // Extract headings and organize hierarchy
  useEffect(() => {
    // Helper function to extract headings with position info
    const extractHeadingsByTag = (regex: RegExp, level: number) => {
      const matches: FlatTOCEntry[] = []
      let match
      while ((match = regex.exec(content)) !== null) {
        const title = match[1].replace(/<[^>]*>/g, '').trim()
        if (title) {
          matches.push({
            id: title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            title,
            level,
            position: match.index,
          })
        }
      }
      return matches
    }

    // Extract only h1 and h2 heading types
    const h1Matches = extractHeadingsByTag(/<h1[^>]*>(.*?)<\/h1>/gi, 1)
    const h2Matches = extractHeadingsByTag(/<h2[^>]*>(.*?)<\/h2>/gi, 2)

    // Combine and sort by position (only h1 and h2)
    let headingMatches = [...h1Matches, ...h2Matches]
    headingMatches.sort((a, b) => a.position - b.position)

    // Add Introduction if missing
    if (!headingMatches.some(entry => entry.title.toLowerCase().includes('introduction'))) {
      headingMatches.unshift({
        id: 'introduction',
        title: 'Introduction',
        level: 1,
        position: 0,
      })
    }

    // Set flat entries
    setFlatEntries(headingMatches)

    // Organize into hierarchy
    const organizeHierarchy = (entries: FlatTOCEntry[]): TOCEntry[] => {
      const result: TOCEntry[] = []
      const currentLevelEntries: {[key: number]: TOCEntry} = {}

      entries.forEach(entry => {
        const newEntry: TOCEntry = {
          ...entry,
          children: [],
        }

        // Find the appropriate parent level
        let parentLevel = entry.level - 1
        while (parentLevel > 0) {
          if (currentLevelEntries[parentLevel]) {
            currentLevelEntries[parentLevel].children.push(newEntry)
            break
          }
          parentLevel--
        }

        // If no parent found, add to root
        if (parentLevel === 0) {
          result.push(newEntry)
        }

        // Update current level entry
        currentLevelEntries[entry.level] = newEntry

        // Clear lower levels when a new heading of this level is encountered
        for (let i = entry.level + 1; i <= 4; i++) {
          delete currentLevelEntries[i]
        }
      })

      return result
    }

    // Set hierarchical entries for rendering
    setTocEntries(organizeHierarchy(headingMatches))

    // Add IDs to headings in the DOM
    setTimeout(() => {
      headingMatches.forEach(entry => {
        const existingElement = document.getElementById(entry.id)
        if (!existingElement) {
          // Find the element by text content and add ID
          const allElements = document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, strong, span')

          for (let element of allElements) {
            const elementText = element.textContent?.trim() || ''
            if (
              elementText === entry.title ||
              elementText.replace(/^\d+\.\s+/, '') === entry.title.replace(/^\d+\.\s+/, '')
            ) {
              element.id = entry.id
              break
            }
          }
        }
      })

      // Add Introduction ID to first paragraph if not exists
      if (!document.getElementById('introduction')) {
        const firstParagraph = document.querySelector('p')
        if (firstParagraph) {
          // Instead of creating a separate div, just add the ID directly to the first paragraph
          firstParagraph.id = 'introduction'
        }
      }
    }, 1000)
  }, [content])

  // Simple scroll function that just works
  const scrollToSection = (id: string) => {
    console.log('Attempting to scroll to:', id) // Debug log

    // Special handling for Introduction - scroll to start of blog content
    if (id === 'introduction') {
      console.log('Scrolling to introduction (start of blog content)')

      // Find the first paragraph or the blog content container
      const firstParagraph = document.querySelector('p')
      const blogContentStart = document.querySelector('[data-blog-content]') || firstParagraph

      if (blogContentStart) {
        const elementTop = blogContentStart.getBoundingClientRect().top
        const offsetTop = window.pageYOffset + elementTop - 150 // 150px offset for navigation

        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        })
      } else {
        // Fallback: scroll to a reasonable position from the top
        window.scrollTo({
          top: 300, // Adjust this value based on your header/banner height
          behavior: 'smooth',
        })
      }

      // Set Introduction as active immediately and keep it active for a bit longer
      setActiveSection(id)

      // Override the intersection observer for Introduction by setting it again after scroll completes
      setTimeout(() => {
        setActiveSection('introduction')
      }, 1000) // Wait for scroll to complete

      return false
    }

    const element = document.getElementById(id)
    console.log('Found element:', element) // Debug log

    if (!element) {
      console.log('Element not found for id:', id)
      return false
    }

    // Simple approach: just scroll to the element with an offset
    const elementTop = element.getBoundingClientRect().top
    const offsetTop = window.pageYOffset + elementTop - 150 // 150px offset for navigation

    console.log('Scrolling to position:', offsetTop) // Debug log

    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth',
    })

    setActiveSection(id)
    return false
  }

  // Fixed intersection observer for active tracking
  useEffect(() => {
    if (flatEntries.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        // Only process entries that are intersecting
        const visibleEntries = entries.filter(entry => entry.isIntersecting)

        if (visibleEntries.length > 0) {
          // Sort by distance from top of viewport
          visibleEntries.sort((a, b) => {
            const aTop = a.boundingClientRect.top
            const bTop = b.boundingClientRect.top
            return Math.abs(aTop) - Math.abs(bTop) // Get the one closest to the top
          })

          const targetSection = visibleEntries[0].target.id

          // Special handling for when we're at the very top - prioritize Introduction
          if (window.scrollY < 200) {
            // If we're near the top of the page
            setActiveSection('introduction')
          } else {
            setActiveSection(targetSection)
          }
        }
      },
      {
        // Smaller top margin so sections activate when they're actually visible
        rootMargin: '-50px 0px -80% 0px',
        threshold: [0.1, 0.3], // Lower threshold values
      },
    )

    // Observe elements after a delay to ensure they exist
    setTimeout(() => {
      flatEntries.forEach(entry => {
        const element = document.getElementById(entry.id)
        if (element) {
          observer.observe(element)
        }
      })
    }, 1500)

    return () => observer.disconnect()
  }, [flatEntries])

  // Helper function to identify main sections
  const isMainSection = (title: string): boolean => {
    return (
      title === 'Introduction' ||
      title === 'What is IT standardization?' ||
      title.includes('Benefits of IT standardization') ||
      title.includes('The multi-location challenge') ||
      title.includes('Implement IT standardization')
    )
  }

  // Render function
  const renderTOCEntries = (entries: TOCEntry[], level = 1) => {
    return entries.map(entry => (
      <React.Fragment key={entry.id}>
        <li
          className={`${classes.tocItem} ${
            !isMainSection(entry.title) && level > 1 ? classes.tocItemIndented : ''
          } ${activeSection === entry.id ? classes.tocItemActive : ''}`}
        >
          <button
            onClick={e => {
              e.preventDefault()
              scrollToSection(entry.id)
            }}
            className={classes.tocLink}
            style={{
              background: 'none',
              border: 'none',
              textAlign: 'left',
              width: '100%',
              cursor: 'pointer',
            }}
          >
            {entry.title}
          </button>
        </li>
        {entry.children.length > 0 && (
          <ul className={classes.tocNestedList}>{renderTOCEntries(entry.children, level + 1)}</ul>
        )}
      </React.Fragment>
    ))
  }

  if (tocEntries.length === 0) {
    return null
  }

  return (
    <div className={classes.tocContainer}>
      <div className={classes.tocHeader}>
        <h2 className={classes.tocTitle}>{title}</h2>
        <button onClick={() => setIsOpen(!isOpen)} className={classes.tocToggle}>
          {isOpen ? (
            <Image src={minusImg} width={18} alt="minus" />
          ) : (
            <Image src={plusImg} width={18} alt="plus" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className={classes.tocContent}>
          <ul className={classes.tocList}>{renderTOCEntries(tocEntries)}</ul>
        </div>
      )}
    </div>
  )
}

export default BlogTOC
